import React, { useState } from 'react';
import { EnvelopeSimple, User, PaperPlaneTilt, MapPin, ChatDots } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    // Simulate send (no backend endpoint needed for now)
    setTimeout(() => {
      toast.success('Message sent! Kimberly will get back to you soon.');
      setForm({ name: '', email: '', subject: '', message: '' });
      setSending(false);
    }, 1000);
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F6] px-4 py-12" data-testid="contact-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-2xl bg-[#FFD166] border-2 border-[#004E89] flex items-center justify-center mx-auto mb-4 shadow-[4px_4px_0px_#004E89]">
            <ChatDots size={32} weight="duotone" className="text-[#004E89]" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#004E89] mb-3" style={{ fontFamily: 'Outfit' }}>
            Get in Touch
          </h1>
          <p className="text-[#5C7A99] text-base md:text-lg max-w-md mx-auto">
            Have a question or need help? We'd love to hear from you.
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-6">
          {/* Admin Info */}
          <div className="md:col-span-2 space-y-4">
            <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]" data-testid="admin-info-card">
              <h2 className="text-lg font-bold text-[#004E89] mb-4" style={{ fontFamily: 'Outfit' }}>Administrator</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#FF6B35] border-2 border-[#004E89] flex items-center justify-center flex-shrink-0">
                    <User size={20} weight="bold" className="text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[#004E89] text-sm" data-testid="admin-name">Kimberly Combs</p>
                    <p className="text-xs text-[#5C7A99]">Site Administrator</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#FFD166] border-2 border-[#004E89] flex items-center justify-center flex-shrink-0">
                    <EnvelopeSimple size={20} weight="bold" className="text-[#004E89]" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#5C7A99] uppercase tracking-wide mb-0.5">Email</p>
                    <a
                      href="mailto:Ltestatesales4all@gmail.com"
                      className="text-sm font-bold text-[#FF6B35] hover:underline break-all"
                      data-testid="admin-email"
                    >
                      Ltestatesales4all@gmail.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#004E89] border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#FFD166]">
              <MapPin size={28} weight="duotone" className="text-[#FFD166] mb-3" />
              <h3 className="text-white font-bold mb-2" style={{ fontFamily: 'Outfit' }}>SaleSeeker</h3>
              <p className="text-[#B8D4E8] text-sm leading-relaxed">
                Your go-to platform for finding estate sales, garage sales, auctions, and more in your local area.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="md:col-span-3">
            <form onSubmit={handleSubmit} className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]" data-testid="contact-form">
              <h2 className="text-lg font-bold text-[#004E89] mb-4" style={{ fontFamily: 'Outfit' }}>Send a Message</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Your Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                    className="input-neo"
                    placeholder="Your name"
                    required
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Your Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                    className="input-neo"
                    placeholder="you@example.com"
                    required
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Subject</label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={(e) => setForm(prev => ({ ...prev, subject: e.target.value }))}
                    className="input-neo"
                    placeholder="What is this about?"
                    required
                    data-testid="contact-subject-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Message</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))}
                    className="input-neo min-h-[120px] resize-y"
                    placeholder="How can we help?"
                    required
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  className="btn-primary w-full py-3 flex items-center justify-center gap-2"
                  data-testid="contact-submit-btn"
                >
                  <PaperPlaneTilt size={20} weight="bold" />
                  {sending ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
